import React, { useCallback, useState } from 'react';
import { ButtonWithIcon } from 'src/components/Buttons';
import { useActivePosEntityContext } from 'src/contexts/ActivePosEntityContext';
import { useAppContext } from 'src/contexts/AppContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { vars } from 'src/core/themes';
import { ButtonProps } from 'src/core/ui';
import { useBasicDialog } from 'src/hooks/useBasicDialog';
import { AdjustFxRateDialog } from 'src/modals/SaleDetails/actions/AdjustFxRateDialog';
import { ExchangeIcon, IconsFill } from 'src/svgs/Viagogo';
import { ContentId } from 'src/utils/constants/contentId';
import { isSuccess } from 'src/utils/errorUtils';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  ActionOutboxEntityType,
  ActivityLogClient,
  ApiException,
  SaleClient,
  SaleDetails,
} from 'src/WebApiController';

// These fields exist on Sale from the backend (origCurcy, convRate, convRateOvr)
// but are not yet in the generated types.ts — remove this extension once types.ts is regenerated
type SaleDetailsWithFx = SaleDetails & {
  origCurcy: string | null;
  convRate: number | null;
  convRateOvr: number | null;
};

export const LaunchUpdateConversionRateOverride = ({
  saleId,
  marketplaceSaleId,
  variant,
  iconOnlyMode,
  disabled,
}: {
  saleId: number;
  marketplaceSaleId?: string | null;
  variant?: ButtonProps['variant'];
  iconOnlyMode?: boolean;
  disabled?: boolean;
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const { showErrorDialog, genericError, trackError } = useClientLogContext();
  const { activeAccountWebClientConfig } = useAppContext();
  const { posEntity, setActivePosEntity } =
    useActivePosEntityContext<SaleDetailsWithFx>();

  const { launchDialog, closeDialog, dialogProps } = useBasicDialog();

  const sale = posEntity;

  // convRateOvr is the user-set override; convRate is the system conversion rate.
  // Default to convRateOvr if present, otherwise fall back to convRate.
  const defaultRate = sale?.convRateOvr ?? sale?.convRate ?? 1;

  const onSubmit = useCallback(
    (newRate: number) => {
      setIsLoading(true);
      tryInvokeApi(
        async () => {
          const client = new SaleClient(activeAccountWebClientConfig);
          const result = await client.updateSaleConversionRateOverride(
            { item1: saleId, item2: newRate },
            String(saleId)
          );
          if (!isSuccess(result)) {
            showErrorDialog(
              'SaleClient.updateSaleConversionRateOverride',
              {
                message: result.message ?? genericError,
                status: result.status!,
              } as ApiException,
              { trackErrorData: { saleId } }
            );
          } else {
            await setActivePosEntity(saleId, marketplaceSaleId, true);
          }
        },
        (error) => {
          showErrorDialog(
            'SaleClient.updateSaleConversionRateOverride',
            error,
            {
              trackErrorData: { saleId },
            }
          );
        },
        () => {
          setIsLoading(false);
          closeDialog();
        }
      );
    },
    [
      activeAccountWebClientConfig,
      closeDialog,
      genericError,
      marketplaceSaleId,
      saleId,
      setActivePosEntity,
      showErrorDialog,
    ]
  );

  const onLaunchDialog = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      tryInvokeApi(
        async () => {
          await new ActivityLogClient(
            activeAccountWebClientConfig
          ).trackEntityView(saleId, ActionOutboxEntityType.Sale);
        },
        (error) => {
          trackError('ActivityLogClient.trackEntityView', error, { saleId });
        }
      );
      launchDialog();
    },
    [launchDialog, activeAccountWebClientConfig, saleId, trackError]
  );

  return (
    <div>
      <ButtonWithIcon
        onClick={onLaunchDialog}
        disabled={disabled || isLoading}
        icon={
          <ExchangeIcon
            withHoverEffect
            fill={
              !iconOnlyMode && variant === 'regular'
                ? IconsFill.textInverted
                : IconsFill.textBrand
            }
            size={vars.iconSize.m}
          />
        }
        iconOnlyMode={iconOnlyMode}
        variant={variant}
        textContentId={ContentId.AdjustFxRate}
      />
      {dialogProps.isOpen && (
        <AdjustFxRateDialog
          isOpen={dialogProps.isOpen}
          defaultRate={defaultRate}
          // mkpSaleTtlNetProcs is on SaleDetails (not Sale) and is always populated
          // when the action is available, since the sale was already converted.
          mkpSaleTtlNetProcs={sale?.mkpSaleTtlNetProcs ?? null}
          origCurcy={sale?.origCurcy ?? null}
          targetCurcy={sale?.ttlNetProcs?.currency ?? null}
          onOkay={onSubmit}
          onCancel={closeDialog}
          isLoading={isLoading}
        />
      )}
    </div>
  );
};
